import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";
import { INDUSTRIES, IMG } from "../data/content";
import { FadeUp } from "../components/Reveal";
import { CTASection, PageHero } from "../components/PageHero";

const INDUSTRY_PHOTOS = {
  restaurants: IMG.restroInterior,
  cafes: IMG.cafe,
  "hotels-resorts": IMG.hotel,
  "pubs-lounges": IMG.pub,
  "cloud-kitchens": IMG.kitchen,
  "event-venues": IMG.eventHall,
};

function IndustryCard({ industry }) {
  const [open, setOpen] = useState(false);
  const frontButton = useRef(null);
  const detailsLink = useRef(null);
  const showDetails = () => {
    setOpen(true);
    requestAnimationFrame(() => detailsLink.current?.focus({ preventScroll: true }));
  };
  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => frontButton.current?.focus({ preventScroll: true }));
  };
  return (
    <article
      className={`industry-flip ${open ? "is-flipped" : ""}`}
      data-testid={`industry-detail-${industry.id}`}
      onPointerEnter={(event) => { if (event.pointerType === "mouse") setOpen(true); }}
      onPointerLeave={(event) => { if (event.pointerType === "mouse" && !event.currentTarget.contains(document.activeElement)) setOpen(false); }}
      onBlur={(event) => { if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}
      onKeyDown={(event) => { if (event.key === "Escape" && open) close(); }}
    >
      <div className="industry-flip-inner">
        <div className="industry-flip-front" aria-hidden={open} inert={open}>
          <img src={INDUSTRY_PHOTOS[industry.id]} alt="" loading="lazy" />
          <div className="industry-flip-caption">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl">{industry.title}</h2>
            <span className="mt-3 inline-flex items-center gap-2 text-sm text-white/90">Explore solutions <ArrowRight className="w-4 h-4" /></span>
          </div>
          <button ref={frontButton} type="button" className="industry-flip-trigger" onClick={showDetails} aria-label={`Explore solutions for ${industry.title}`} aria-expanded={open} aria-controls={`industry-back-${industry.id}`} />
        </div>
        <div className="industry-flip-back" id={`industry-back-${industry.id}`} aria-hidden={!open} inert={!open}>
          <span className="font-mono uppercase tracking-[0.2em] text-xs text-saffron-warm">Built for your business</span>
          <h3 className="mt-4 font-display font-extrabold text-2xl sm:text-3xl">{industry.title}</h3>
          <p className="mt-4 text-sm sm:text-base text-white/90 leading-relaxed">{industry.desc}</p>
          <Link ref={detailsLink} to="/services" className="mt-auto pt-6 inline-flex items-center gap-2 font-bold text-saffron-warm hover:text-white">
            Explore services <ArrowRight className="w-4 h-4" />
          </Link>
          <button type="button" className="mt-5 inline-flex self-start items-center gap-2 text-sm text-white/80 hover:text-white" onClick={close}>
            <RotateCcw className="w-4 h-4" /> Back to photo
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Industries() {
  return (
    <main data-testid="industries-page">
      <PageHero
        overline="Industries We Serve"
        lines={["Built for every kind of", "hospitality business"]}
        desc="A restaurant needs different marketing from a software company — that's why everything we do is built around hospitality. Pick your world."
        img={IMG.restroInterior}
        imgAlt="Warm restaurant dining room"
        accent="#3B5A40"
      />
      <section className="hospitality-background py-12 sm:py-16 lg:py-20 relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid md:grid-cols-2 gap-6">
          {INDUSTRIES.map((ind, i) => (
              <FadeUp key={ind.id} delay={i * 0.06}>
                <IndustryCard industry={ind} />
              </FadeUp>
          ))}
        </div>
      </section>
      <CTASection
        title="Whatever you run, we know how to grow it"
        sub="Tell us about your venue — we'll recommend the right mix of digital, creative and hospitality solutions."
      />
    </main>
  );
}
